import { supabase } from '@/integrations/supabase/client';
import { isTokenExpired } from './tokenService';

// Debug helper - only logs in development
const logDebug = (message: string, data?: any) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[SessionService] ${message}`, data || '');
  }
};

// Clear all session-related data from localStorage
export const clearSessionData = (): void => {
  try {
    localStorage.removeItem('user');
    localStorage.removeItem('lastActivityTimestamp');
    localStorage.removeItem('redirectAfterLogin');
    logDebug('Session data cleared');
  } catch (e) {
    logDebug('Error clearing session data:', e);
  }
};

// Update the activity timestamp to keep session alive
export const updateLastActivityTimestamp = (): void => {
  try {
    const timestamp = Date.now().toString();
    localStorage.setItem('lastActivityTimestamp', timestamp);
    logDebug('Activity timestamp updated', new Date(parseInt(timestamp)).toISOString());
  } catch (e) {
    logDebug('Error updating activity timestamp:', e);
  }
};

// Check if session has timed out due to inactivity
export const hasSessionTimedOut = (timeoutMinutes = 60): boolean => {
  try {
    const lastActivityTime = parseInt(localStorage.getItem('lastActivityTimestamp') || '0', 10);
    const currentTime = Date.now();
    const sessionTimeout = timeoutMinutes * 60 * 1000; // Convert minutes to milliseconds
    const timeSinceLastActivity = currentTime - lastActivityTime;
    
    logDebug('Checking session timeout', {
      lastActivity: new Date(lastActivityTime).toISOString(),
      current: new Date(currentTime).toISOString(),
      timeout: `${timeoutMinutes} minutes`,
      timeSinceLastActivity: `${Math.round(timeSinceLastActivity/1000/60)} minutes`
    });
    
    return timeSinceLastActivity > sessionTimeout;
  } catch (e) {
    logDebug('Error checking session timeout:', e);
    return false; // Default to not timed out on error
  }
};

// Store current path for redirect after login
export const storeRedirectPath = (path: string): void => {
  try {
    if (path !== '/login' && path !== '/signup') {
      localStorage.setItem('redirectAfterLogin', path);
      logDebug('Stored redirect path', path);
    }
  } catch (e) {
    logDebug('Error storing redirect path:', e);
  }
};

// Get redirect path after login
export const getRedirectPath = (): string => {
  try {
    const path = localStorage.getItem('redirectAfterLogin') || '/';
    logDebug('Retrieved redirect path', path);
    return path;
  } catch (e) {
    logDebug('Error getting redirect path:', e);
    return '/';
  }
};

// Attempts to refresh the session
export const refreshSession = async (): Promise<boolean> => {
  try {
    logDebug('Attempting to refresh session...');
    const { data, error } = await supabase.auth.refreshSession();
    
    if (error) {
      logDebug('Error refreshing session:', error);
      return false;
    }
    
    if (!data.session) {
      logDebug('No session returned after refresh');
      return false;
    }
    
    // Check if the refreshed token is valid
    if (isTokenExpired(data.session.access_token)) {
      logDebug('Refreshed token is still expired');
      return false;
    }
    
    // Update last activity timestamp
    updateLastActivityTimestamp();
    logDebug('Session refreshed successfully');
    return true;
  } catch (error) {
    logDebug('Exception in refreshSession:', error);
    return false;
  }
};

// Checks if there is a valid session
export const hasValidSession = async (): Promise<boolean> => {
  try {
    logDebug('Checking session validity');
    const { data, error } = await supabase.auth.getSession();
    
    if (error) {
      logDebug('Error checking session validity:', error);
      return false;
    }
    
    // Check if we have a session
    if (!data.session) {
      logDebug('No session found');
      return false;
    }
    
    // Explicitly check token expiration
    const accessToken = data.session.access_token;
    if (isTokenExpired(accessToken)) {
      logDebug('JWT token is expired, attempting refresh');
      
      // Try to refresh the token
      const refreshed = await refreshSession();
      if (!refreshed) {
        logDebug('Token refresh failed, clearing session data');
        clearSessionData();
        return false;
      }
      
      logDebug('Token refreshed successfully');
      return true;
    }
    
    // If we reach here, the session is valid
    // Update the last activity timestamp
    updateLastActivityTimestamp();
    logDebug('Session is valid');
    return true;
  } catch (error) {
    logDebug('Exception in hasValidSession:', error);
    return false;
  }
};

// Sets up activity monitors to update the timestamp
export const setupActivityMonitors = (): (() => void) => {
  // Function to handle user activity
  const handleUserActivity = () => {
    updateLastActivityTimestamp();
  };
  
  // Attach listeners for common user interactions
  window.addEventListener('mousedown', handleUserActivity);
  window.addEventListener('keydown', handleUserActivity);
  window.addEventListener('touchstart', handleUserActivity);
  window.addEventListener('scroll', handleUserActivity);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('mousedown', handleUserActivity);
    window.removeEventListener('keydown', handleUserActivity);
    window.removeEventListener('touchstart', handleUserActivity);
    window.removeEventListener('scroll', handleUserActivity);
    logDebug('Activity monitors removed');
  };
};
